/**
 * Portal Analytics Dashboard — Admin page for viewing portal user
 * engagement data: overview stats, user activity, event timeline,
 * user journey sequences, and feature adoption read from the audit
 * truth tables (order edits, Edit-in-Sage, co-pilot, Kirk drafting).
 */

import { useEffect, useState, useCallback } from 'react';
import { Tabs, TabList, Tab, TabPanel } from '../components/common';
import {
  portalAnalyticsService,
  type OverviewStats,
  type UserActivityResponse,
  type TimelineResponse,
  type SequenceResponse,
  type CommonPatternsResponse,
  type AnalyticsUser,
} from '../services/portalAnalytics';
import styles from './PortalAnalyticsDashboard.module.css';
import { FeatureAdoptionPanel } from './portalAnalytics/FeatureAdoptionPanel';

// --- Color helpers ---

const EVENT_TYPE_COLORS: Record<string, string> = {
  click: styles.barClick,
  navigation: styles.barNavigation,
  search: styles.barSearch,
  action: styles.barAction,
  view: styles.barView,
  filter: styles.barFilter,
};

const EVENT_TYPE_BADGES: Record<string, string> = {
  click: styles.badgeBlue,
  navigation: styles.badgeGreen,
  search: styles.badgeYellow,
  action: styles.badgeRed,
  view: styles.badgePurple,
  filter: styles.badgePink,
};

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortPage(page: string): string {
  return page.replace('/portal/', '').replace('/', '') || 'home';
}

// --- Main component ---

export function PortalAnalyticsDashboard() {
  const [days, setDays] = useState(7);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [activeUsers, setActiveUsers] = useState<AnalyticsUser[]>([]);
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [userActivity, setUserActivity] = useState<UserActivityResponse | null>(null);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [sequences, setSequences] = useState<SequenceResponse | null>(null);
  const [commonPatterns, setCommonPatterns] = useState<CommonPatternsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Controlled so a period / user change (which refetches) keeps the reader on
  // the tab they were looking at instead of dropping them back to Overview.
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch the list of users who have portal events (for the filter dropdown)
  useEffect(() => {
    portalAnalyticsService.getActiveUsers(90).then((resp) => {
      setActiveUsers(resp.users);
    }).catch(() => {
      // Non-critical — dropdown just stays empty
    });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const uid = selectedUserId || undefined;
      const [ov, ua, tl, seq, cp] = await Promise.all([
        portalAnalyticsService.getOverview(days, uid),
        portalAnalyticsService.getUserActivity(days, uid),
        portalAnalyticsService.getTimeline(days, days <= 3 ? 'hour' : 'day', uid),
        portalAnalyticsService.getSequences(days, 10, uid),
        portalAnalyticsService.getCommonPatterns(days, 1, uid),
      ]);
      setOverview(ov);
      setUserActivity(ua);
      setTimeline(tl);
      setSequences(seq);
      setCommonPatterns(cp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  }, [days, selectedUserId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Full-page spinner only before the first payload: later refetches keep the
  // page (and the self-fetching Feature Adoption panel) mounted.
  if (isLoading && !overview) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        Loading analytics...
      </div>
    );
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>Portal Analytics</h1>
        </div>
        <div className={styles.controls}>
          {isLoading && <span role="status" style={{ fontSize: '0.8125rem', color: 'var(--cm-gray-500)' }}>Refreshing…</span>}
          <select
            className={styles.periodSelect}
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
          >
            <option value="">All Users</option>
            {activeUsers.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.user_name} ({u.user_role})
              </option>
            ))}
          </select>
          <select
            className={styles.periodSelect}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={1}>Last 24 hours</option>
            <option value={3}>Last 3 days</option>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Stat cards */}
      {overview && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{overview.total_events.toLocaleString()}</span>
            <span className={styles.statLabel}>Total Events</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{overview.active_users}</span>
            <span className={styles.statLabel}>Active Users</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{overview.unique_sessions}</span>
            <span className={styles.statLabel}>Sessions</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statValue}>
              {overview.total_events && overview.unique_sessions
                ? Math.round(overview.total_events / overview.unique_sessions)
                : 0}
            </span>
            <span className={styles.statLabel}>Events / Session</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
        <TabList>
          <Tab value="overview">Overview</Tab>
          <Tab value="users">User Activity</Tab>
          <Tab value="sequences">Sequences</Tab>
          <Tab value="adoption">Feature Adoption</Tab>
        </TabList>

        {/* --- Overview Tab --- */}
        <TabPanel value="overview">
          {overview && (
            <>
              {/* Timeline chart */}
              {timeline && timeline.buckets.length > 0 && (
                <div className={styles.chartContainer}>
                  <h3 className={styles.chartTitle}>
                    Event Volume ({timeline.granularity === 'hour' ? 'Hourly' : 'Daily'})
                  </h3>
                  <div className={styles.timelineChart}>
                    {(() => {
                      const maxCount = Math.max(...timeline.buckets.map((b) => b.count), 1);
                      return timeline.buckets.map((b, i) => (
                        <div
                          key={i}
                          className={styles.timelineBar}
                          style={{ height: `${(b.count / maxCount) * 100}%` }}
                          data-tooltip={`${formatTimestamp(b.bucket)}: ${b.count} events, ${b.unique_users} users`}
                          title={`${formatTimestamp(b.bucket)}: ${b.count} events, ${b.unique_users} users`}
                        />
                      ));
                    })()}
                  </div>
                </div>
              )}

              {/* Two column: event types + top pages */}
              <div className={styles.twoCol}>
                <div className={styles.chartContainer}>
                  <h3 className={styles.chartTitle}>Event Types</h3>
                  {overview.event_type_breakdown.length > 0 ? (
                    <div className={styles.barChart}>
                      {(() => {
                        const maxVal = Math.max(...overview.event_type_breakdown.map((e) => e.count), 1);
                        return overview.event_type_breakdown.map((e) => (
                          <div key={e.event_type} className={styles.barRow}>
                            <span className={styles.barLabel}>{e.event_type}</span>
                            <div className={styles.barTrack}>
                              <div
                                className={`${styles.barFill} ${EVENT_TYPE_COLORS[e.event_type] || styles.barDefault}`}
                                style={{ width: `${(e.count / maxVal) * 100}%` }}
                              />
                            </div>
                            <span className={styles.barCount}>{e.count}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>No events recorded yet</div>
                  )}
                </div>

                <div className={styles.chartContainer}>
                  <h3 className={styles.chartTitle}>Top Pages</h3>
                  {overview.top_pages.length > 0 ? (
                    <div className={styles.barChart}>
                      {(() => {
                        const maxVal = Math.max(...overview.top_pages.map((p) => p.count), 1);
                        return overview.top_pages.map((p) => (
                          <div key={p.page} className={styles.barRow}>
                            <span className={styles.barLabel}>{shortPage(p.page)}</span>
                            <div className={styles.barTrack}>
                              <div
                                className={`${styles.barFill} ${styles.barDefault}`}
                                style={{ width: `${(p.count / maxVal) * 100}%` }}
                              />
                            </div>
                            <span className={styles.barCount}>{p.count}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>No page data yet</div>
                  )}
                </div>
              </div>

              {/* Top interaction targets */}
              <div className={styles.chartContainer}>
                <h3 className={styles.chartTitle}>Top Interaction Targets</h3>
                {overview.top_targets.length > 0 ? (
                  <div className={styles.barChart}>
                    {(() => {
                      const maxVal = Math.max(...overview.top_targets.map((t) => t.count), 1);
                      return overview.top_targets.map((t) => (
                        <div key={t.event_target} className={styles.barRow}>
                          <span className={styles.barLabel}>{t.event_target}</span>
                          <div className={styles.barTrack}>
                            <div
                              className={`${styles.barFill} ${styles.barDefault}`}
                              style={{ width: `${(t.count / maxVal) * 100}%` }}
                            />
                          </div>
                          <span className={styles.barCount}>{t.count}</span>
                        </div>
                      ));
                    })()}
                  </div>
                ) : (
                  <div className={styles.emptyState}>No target data yet</div>
                )}
              </div>
            </>
          )}
        </TabPanel>

        {/* --- User Activity Tab --- */}
        <TabPanel value="users">
          {userActivity && userActivity.users.length > 0 ? (
            <div className={styles.chartContainer}>
              <h3 className={styles.chartTitle}>User Activity ({userActivity.total_users} users)</h3>
              <table className={styles.userTable}>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Events</th>
                    <th>Sessions</th>
                    <th>Top Action</th>
                    <th>First Seen</th>
                    <th>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {userActivity.users.map((u) => (
                    <tr key={u.user_id}>
                      <td>
                        <div>{u.user_name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--cm-gray-500)' }}>
                          {u.user_email}
                        </div>
                      </td>
                      <td>
                        <span className={`${styles.badge} ${styles.badgeGray}`}>{u.user_role}</span>
                      </td>
                      <td><strong>{u.total_events}</strong></td>
                      <td>{u.sessions}</td>
                      <td>
                        <span className={`${styles.badge} ${EVENT_TYPE_BADGES[u.top_event_type] || styles.badgeGray}`}>
                          {u.top_event_type}
                        </span>
                      </td>
                      <td>{formatTimestamp(u.first_seen)}</td>
                      <td>{formatTimestamp(u.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.emptyState}>No user activity in this period</div>
          )}
        </TabPanel>

        {/* --- Sequences Tab --- */}
        <TabPanel value="sequences">
          {/* Common patterns */}
          {commonPatterns && commonPatterns.patterns.length > 0 && (
            <div className={styles.chartContainer}>
              <h3 className={styles.chartTitle}>
                Common Patterns ({commonPatterns.total_sessions_analyzed} sessions analyzed)
              </h3>
              <div className={styles.patternList}>
                {commonPatterns.patterns.map((p, idx) => (
                  <div key={idx} className={styles.patternCard}>
                    <div className={styles.patternHeader}>
                      <span className={styles.patternRank}>#{idx + 1}</span>
                      <span className={styles.patternStats}>
                        <strong>{p.session_count}</strong> session{p.session_count !== 1 ? 's' : ''}
                        {' · '}
                        {p.unique_users} user{p.unique_users !== 1 ? 's' : ''}
                        {p.avg_duration_seconds != null && (
                          <> · avg {formatDuration(p.avg_duration_seconds)}</>
                        )}
                        {p.avg_events_per_session != null && (
                          <> · ~{p.avg_events_per_session} events</>
                        )}
                      </span>
                    </div>
                    <div className={styles.patternFlow}>
                      {p.pattern.map((step, si) => (
                        <span key={si}>
                          {si > 0 && <span className={styles.journeyArrow}> → </span>}
                          <span className={styles.patternStep}>
                            <span className={`${styles.badge} ${EVENT_TYPE_BADGES[step.event_type] || styles.badgeGray}`}>
                              {step.event_type}
                            </span>
                            {shortPage(step.page)}
                          </span>
                        </span>
                      ))}
                    </div>
                    {p.example_users.length > 0 && (
                      <div className={styles.patternUsers}>
                        Users: {p.example_users.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {sequences && (
            <>
              {/* Page transitions */}
              <div className={styles.chartContainer}>
                <h3 className={styles.chartTitle}>Page Transitions</h3>
                {sequences.transitions.length > 0 ? (
                  <table className={styles.transitionTable}>
                    <thead>
                      <tr>
                        <th>From</th>
                        <th></th>
                        <th>To</th>
                        <th>Count</th>
                        <th>Avg Dwell</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sequences.transitions.map((t, i) => (
                        <tr key={i}>
                          <td>
                            <span className={`${styles.badge} ${styles.badgeBlue}`}>
                              {shortPage(t.from_page)}
                            </span>
                          </td>
                          <td className={styles.arrow}>→</td>
                          <td>
                            <span className={`${styles.badge} ${styles.badgeGreen}`}>
                              {shortPage(t.to_page)}
                            </span>
                          </td>
                          <td><strong>{t.count}</strong></td>
                          <td>
                            {t.avg_dwell_seconds != null
                              ? formatDuration(t.avg_dwell_seconds)
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className={styles.emptyState}>
                    Not enough navigation data for transitions yet
                  </div>
                )}
              </div>

              {/* Recent journeys */}
              <div className={styles.chartContainer}>
                <h3 className={styles.chartTitle}>Recent User Journeys</h3>
                {sequences.recent_journeys.length > 0 ? (
                  <div className={styles.journeyList}>
                    {sequences.recent_journeys.map((j) => (
                      <div key={j.session_id} className={styles.journeyCard}>
                        <div className={styles.journeyHeader}>
                          <span className={styles.journeyUser}>
                            {j.user_name}
                            <span style={{ fontWeight: 400, color: 'var(--cm-gray-500)', marginLeft: 8, fontSize: '0.8125rem' }}>
                              {j.user_email}
                            </span>
                          </span>
                          <span className={styles.journeyMeta}>
                            {formatTimestamp(j.started_at)} · {formatDuration(j.duration_seconds)} · {j.events.length} events
                          </span>
                        </div>
                        <div className={styles.journeySteps}>
                          {j.events.map((e, i) => (
                            <span key={i}>
                              {i > 0 && <span className={styles.journeyArrow}> → </span>}
                              <span
                                className={styles.journeyStep}
                                title={`${e.event_type}: ${e.event_target} on ${e.page}`}
                              >
                                <span className={`${styles.badge} ${EVENT_TYPE_BADGES[e.event_type] || styles.badgeGray}`}>
                                  {e.event_type}
                                </span>
                                {shortPage(e.page)}
                                {e.event_target !== 'page_view' && ` · ${e.event_target}`}
                              </span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    No multi-event sessions recorded yet
                  </div>
                )}
              </div>
            </>
          )}
        </TabPanel>

        {/* --- Feature Adoption Tab (truth tables, not portal_events) --- */}
        <TabPanel value="adoption">
          <FeatureAdoptionPanel days={days} userId={selectedUserId || undefined} />
        </TabPanel>
      </Tabs>
    </div>
  );
}
